import React, { useState } from "react";
import {
    Box, Typography, TextField, Button, Alert, Divider,
    CircularProgress, IconButton, InputAdornment, styled, useTheme
} from "@mui/material";

import { motion } from "framer-motion";
import GoogleIcon from "@mui/icons-material/Google";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import EmailIcon from "@mui/icons-material/Email";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { loginUser, loginWithGoogle } from "../services/authService";
import { useNavigate } from "react-router-dom";
import { checkIfAdmin } from "../utils/checkIfAdmin";
import { auth } from "../firebase/firebaseConfig";

// ==========================================
// 1. STYLED COMPONENTS (Theme-Aware)
// ==========================================

const LoginWrapper = styled(Box)(({ theme }) => ({
    width: "100%",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.background.default, // Globally aware
    transition: "background-color 0.4s ease-in-out",
    padding: theme.spacing(2),
}));

const FormContainer = styled(Box)(({ theme }) => {
    const isSky = theme.palette.background.default === "#BBE0EF";
    return {
        display: "flex",
        flexDirection: "row",
        width: "100%",
        maxWidth: 1100,
        backgroundColor: "#FFFFFF",
        borderRadius: "24px",
        overflow: "hidden",
        boxShadow: isSky
            ? "0 20px 60px rgba(9, 18, 44, 0.15)"
            : "0 8px 30px rgba(0,0,0,0.2)",
        [theme.breakpoints.down("md")]: {
            flexDirection: "column",
        },
    };
});

const IllustrationSide = styled(Box)(({ theme }) => ({
    flex: 1,
    background: `
        linear-gradient(rgba(9, 18, 44, 0.6), rgba(9, 18, 44, 0.7)),
        url("https://images.unsplash.com/photo-1497215728101-856f4ea42174?q=80&w=1170&auto=format&fit=crop")
    `,
    backgroundSize: "cover",
    backgroundPosition: "center",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: "white",
    padding: theme.spacing(6),
    [theme.breakpoints.down("md")]: {
        display: "none",
    },
}));

// ==========================================
// 2. MAIN COMPONENT
// ==========================================

export default function Login() {
    const theme = useTheme();
    const isSkyMode = theme.palette.background.default === "#BBE0EF";

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [redirecting, setRedirecting] = useState(false);

    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");

        try {
            await loginUser(email, password);
            const uid = auth.currentUser?.uid;
            const isAdmin = await checkIfAdmin(uid);

            setRedirecting(true);
            setTimeout(() => {
                navigate(isAdmin ? "/admin-dashboard" : "/user-dashboard");
            }, 1200);
        } catch (err) {
            setError(err.message || "Login failed. Try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setLoading(true);
        setError("");

        try {
            await loginWithGoogle();
            const uid = auth.currentUser?.uid;
            const isAdmin = await checkIfAdmin(uid);

            setRedirecting(true);
            setTimeout(() => {
                navigate(isAdmin ? "/admin-dashboard" : "/user-dashboard");
            }, 1200);
        } catch (err) {
            setError(err.message || "Google login failed. Try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <LoginWrapper>
            <FormContainer>
                {/* LEFT COLUMN — Illustration */}
                <IllustrationSide>
                    <motion.div
                        initial={{ opacity: 0, x: -40 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        style={{ textAlign: "center" }}
                    >
                        <Typography variant="h3" fontWeight={900} sx={{ mb: 2, letterSpacing: "-0.02em" }}>
                            Welcome Back
                        </Typography>
                        <Box sx={{ width: 60, height: 4, bgcolor: "#B8860B", mx: "auto", mb: 3, borderRadius: 2 }} />
                        <Typography variant="h6" sx={{ maxWidth: 400, opacity: 0.9, fontWeight: 400 }}>
                            Continue your learning journey and unlock new professional opportunities.
                        </Typography>
                    </motion.div>
                </IllustrationSide>

                {/* RIGHT COLUMN — Login Form */}
                <Box sx={{ flex: 1, p: { xs: 4, md: 8 }, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                    >
                        <Typography variant="h4" fontWeight={900} sx={{ mb: 1, color: "#09122C", letterSpacing: "-0.03em" }}>
                            Sign In
                        </Typography>

                        <Typography variant="body1" sx={{ mb: 4, color: isSkyMode ? "#09122C" : "text.secondary", fontWeight: 500 }}>
                            Enter your credentials to access your courses
                        </Typography>

                        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: "12px" }}>{error}</Alert>}
                        {redirecting && <Alert severity="info" sx={{ mb: 3, borderRadius: "12px" }}>Securing connection...</Alert>}

                        {/* Google Login */}
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<GoogleIcon />}
                            onClick={handleGoogleLogin}
                            disabled={loading}
                            sx={{
                                py: 1.5,
                                borderRadius: "12px",
                                color: "#09122C",
                                borderColor: "rgba(9, 18, 44, 0.2)",
                                fontWeight: 700,
                                mb: 3,
                                "&:hover": { borderColor: "#09122C", bgcolor: "rgba(9, 18, 44, 0.05)" }
                            }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : "Continue with Google"}
                        </Button>

                        <Divider sx={{ mb: 3, "&::before, &::after": { borderColor: "rgba(0,0,0,0.1)" } }}>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>OR EMAIL</Typography>
                        </Divider>

                        {/* Email Login Form */}
                        <form onSubmit={handleLogin}>
                            <TextField
                                fullWidth
                                label="Email Address"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                variant="outlined"
                                InputProps={{
                                    startAdornment: <EmailIcon sx={{ mr: 1.5, color: "#B8860B" }} />,
                                }}
                                sx={{
                                    mb: 2,
                                    "& .MuiOutlinedInput-root": { borderRadius: "12px" }
                                }}
                            />

                            <TextField
                                fullWidth
                                label="Password"
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                InputProps={{
                                    startAdornment: <LockOpenIcon sx={{ mr: 1.5, color: "#B8860B" }} />,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    mb: 4,
                                    "& .MuiOutlinedInput-root": { borderRadius: "12px" }
                                }}
                            />

                            <Button
                                fullWidth
                                type="submit"
                                variant="contained"
                                disabled={loading}
                                sx={{
                                    backgroundColor: "#09122C",
                                    color: "white",
                                    fontWeight: 800,
                                    py: 1.8,
                                    borderRadius: "12px",
                                    fontSize: "1rem",
                                    boxShadow: "0 10px 20px rgba(9, 18, 44, 0.2)",
                                    "&:hover": { backgroundColor: "#142850", transform: "translateY(-2px)" },
                                    transition: "all 0.3s ease"
                                }}
                            >
                                {loading ? <CircularProgress size={24} color="inherit" /> : "Login to Account"}
                            </Button>
                        </form>
                    </motion.div>
                </Box>
            </FormContainer>
        </LoginWrapper>
    );
}
